/**
 * Input Sanitization and Validation Utilities
 * Addresses BUG-H001 (XSS Prevention) and BUG-M002 (Input Length Validation)
 */

// Max length constraints for various input fields
export const INPUT_LIMITS = {
  FULL_NAME: 100,
  EMAIL: 254, // RFC 5321
  JOB_TITLE: 100,
  COMPANY: 100,
  BIO: 500,
  FEEDBACK_TEXT: 10000,
  MESSAGE: 2000,
  SUBJECT: 200,
} as const;

/**
 * Sanitizes user input by removing potentially dangerous HTML/script tags
 * This is a defense-in-depth measure - React already escapes by default
 * IMPORTANT: Preserves internal spaces (only trims leading/trailing)
 */
export function sanitizeInput(input: string): string {
  if (!input) return '';

  return input
    // Remove script tags and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove event handlers (onclick, onerror, etc.)
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/on\w+\s*=\s*[^\s>]*/gi, '')
    // Remove javascript: protocol
    .replace(/javascript:/gi, '')
    // Remove data: protocol (can be used for XSS)
    .replace(/data:text\/html/gi, '')
    // Remove iframe tags
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    // Remove object and embed tags
    .replace(/<(object|embed)[^>]*>/gi, '')
    // Normalize multiple spaces to single space (but keep single spaces)
    .replace(/\s+/g, ' ')
    // Trim leading/trailing whitespace only
    .trim();
}

/**
 * Sanitizes user input for real-time typing without trimming trailing spaces
 * Used during onChange events to allow natural typing flow
 */
export function sanitizeInputRealtime(input: string): string {
  if (!input) return '';

  return input
    // Remove script tags and their content
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    // Remove event handlers (onclick, onerror, etc.)
    .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/on\w+\s*=\s*[^\s>]*/gi, '')
    // Remove javascript: protocol
    .replace(/javascript:/gi, '')
    // Remove data: protocol (can be used for XSS)
    .replace(/data:text\/html/gi, '')
    // Remove iframe tags
    .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
    // Remove object and embed tags
    .replace(/<(object|embed)[^>]*>/gi, '')
    // Normalize multiple spaces to single space (but keep single spaces)
    .replace(/\s+/g, ' ');
    // NO .trim() - allows user to type spaces naturally
}

/**
 * Validates input length against defined limits
 */
export function validateLength(
  input: string,
  maxLength: number
): { valid: boolean; error?: string } {
  if (!input) return { valid: true };

  if (input.length > maxLength) {
    return {
      valid: false,
      error: `Maximum ${maxLength} characters allowed (${input.length} entered)`
    };
  }

  return { valid: true };
}

/**
 * Comprehensive input validation combining sanitization and length checks
 */
export function validateInput(
  input: string,
  options: {
    maxLength: number;
    fieldName: string;
    required?: boolean;
  }
): { valid: boolean; sanitized: string; error?: string } {
  const { maxLength, fieldName, required = false } = options;

  // Check if required field is empty
  if (required && !input?.trim()) {
    return {
      valid: false,
      sanitized: '',
      error: `${fieldName} is required`
    };
  }

  // Sanitize the input
  const sanitized = sanitizeInput(input);

  // Check length
  const lengthCheck = validateLength(sanitized, maxLength);
  if (!lengthCheck.valid) {
    return {
      valid: false,
      sanitized,
      error: lengthCheck.error
    };
  }

  return { valid: true, sanitized };
}

/**
 * Password validation (BUG-M001)
 */
export interface PasswordValidation {
  valid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
}

export function validatePassword(password: string): PasswordValidation {
  const errors: string[] = [];
  let strength: 'weak' | 'medium' | 'strong' = 'weak';

  if (!password) {
    return { valid: false, errors: ['Password is required'], strength: 'weak' };
  }

  // Minimum length
  if (password.length < 8) {
    errors.push('At least 8 characters');
  }

  // At least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    errors.push('One uppercase letter');
  }

  // At least one lowercase letter
  if (!/[a-z]/.test(password)) {
    errors.push('One lowercase letter');
  }

  // At least one number
  if (!/[0-9]/.test(password)) {
    errors.push('One number');
  }

  // At least one special character
  if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~;']/.test(password)) {
    errors.push('One special character (!@#$%^&*...)');
  }

  // Determine strength
  if (errors.length === 0) {
    if (password.length >= 12) {
      strength = 'strong';
    } else {
      strength = 'medium';
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    strength
  };
}

/**
 * Checks for common weak passwords
 */
const COMMON_WEAK_PASSWORDS = [
  'password', 'password123', '12345678', 'qwerty', 'abc123',
  'letmein', 'welcome', 'monkey', '1234567890', 'Password1'
];

export function isCommonWeakPassword(password: string): boolean {
  return COMMON_WEAK_PASSWORDS.some(weak =>
    password.toLowerCase().includes(weak.toLowerCase())
  );
}

/**
 * Email validation (BUG-H001 FIX: Enhanced validation)
 */
export function validateEmail(email: string): { valid: boolean; error?: string } {
  if (!email) {
    return { valid: false, error: 'Email is required' };
  }

  // Trim whitespace
  email = email.trim().toLowerCase();

  // Basic RFC 5322 compliant regex
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Invalid email format' };
  }

  // Additional validation checks
  const parts = email.split('@');
  if (parts.length !== 2) {
    return { valid: false, error: 'Invalid email format' };
  }

  const [localPart, domain] = parts;

  // Validate local part (before @)
  if (localPart.length === 0 || localPart.length > 64) {
    return { valid: false, error: 'Invalid email format' };
  }

  // Check for consecutive dots
  if (localPart.includes('..') || domain.includes('..')) {
    return { valid: false, error: 'Invalid email format' };
  }

  // Validate domain part (after @)
  if (domain.length === 0 || domain.length > 255) {
    return { valid: false, error: 'Invalid email format' };
  }

  // Check domain has valid TLD
  const domainParts = domain.split('.');
  if (domainParts.length < 2) {
    return { valid: false, error: 'Invalid email domain' };
  }

  const tld = domainParts[domainParts.length - 1];
  if (tld.length < 2) {
    return { valid: false, error: 'Invalid email domain' };
  }

  // Block common typos and invalid domains
  const invalidDomains = ['test.com', 'example.com', 'localhost'];
  if (invalidDomains.includes(domain)) {
    return { valid: false, error: 'Please use a valid email address' };
  }

  if (email.length > INPUT_LIMITS.EMAIL) {
    return { valid: false, error: 'Email is too long' };
  }

  return { valid: true };
}

/**
 * Character counter helper for UI
 */
export function getCharacterCount(input: string, maxLength: number): {
  count: number;
  remaining: number;
  percentage: number;
  warning: boolean;
} {
  const count = input?.length || 0;
  const remaining = maxLength - count;
  const percentage = (count / maxLength) * 100;
  const warning = percentage > 80; // Show warning when 80% full

  return { count, remaining, percentage, warning };
}

/**
 * LinkedIn URL validation
 */
export function validateLinkedInUrl(url: string): { valid: boolean; error?: string; normalizedUrl?: string } {
  if (!url || url.trim() === '') {
    return { valid: true }; // Optional field
  }

  // Trim whitespace
  url = url.trim();

  // Add protocol if missing for validation
  let urlToValidate = url;
  if (!urlToValidate.startsWith('http://') && !urlToValidate.startsWith('https://')) {
    urlToValidate = 'https://' + urlToValidate;
  }

  // Basic URL validation
  try {
    const urlObj = new URL(urlToValidate);

    // Check if it's a LinkedIn URL
    const validDomains = ['linkedin.com', 'www.linkedin.com'];
    if (!validDomains.includes(urlObj.hostname)) {
      return { valid: false, error: 'Please enter a valid LinkedIn profile URL' };
    }

    // Check if it's a profile URL (starts with /in/)
    if (!urlObj.pathname.startsWith('/in/')) {
      return { valid: false, error: 'Please enter a LinkedIn profile URL (e.g., www.linkedin.com/in/yourname)' };
    }

    // Return normalized URL without protocol (www.linkedin.com/in/username format)
    const normalizedUrl = urlObj.hostname + urlObj.pathname;
    return { valid: true, normalizedUrl };
  } catch (e) {
    return { valid: false, error: 'Please enter a valid URL' };
  }
}
